import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import PlatformItems from './PlatformItems/PlatformItems';
import { Loading, PageSubTitle } from 'components/common/Elements/Elements';

import { postRequest } from 'utils/requests';
import { statusColor, chooseStatusOnGoing } from 'utils/common';
import { access, hasAccess, resources } from 'utils/permissions';

const Platform = ({ customerId }) => {
  const [platformData, setPlatformData] = useState({});
  const [loader, setLoader] = useState(true);

  const { t } = useTranslation(['activity']);

  useEffect(() => {
    let cancelled = false;
    postRequest('/customer/platform', {
      customerId: customerId
    })
      .then(res => {
        if (!cancelled) {
          setPlatformData(res.body);
          setLoader(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.log(err);
          setLoader(false);
        }
      });
    return () => (cancelled = true);
  }, [customerId]);

  const teamDeskription = () => {
    if (Object.prototype.hasOwnProperty.call(platformData, 'team')) {
      return (
        platformData.team.members +
        t('activity:platform.MEMBERS_D') +
        platformData.team.managers +
        t('activity:platform.MANAGERS_D') +
        platformData.team.groups +
        t('activity:platform.GROUPS_D')
      );
    } else {
      return t('activity:platform.TEAM_DESKRIPTION');
    }
  };

  const networkDeskription = () => {
    if (Object.prototype.hasOwnProperty.call(platformData, 'networks')) {
      return (
        platformData.networks.networks +
        t('activity:platform.NETWORKS_D') +
        platformData.networks.gateways +
        t('activity:platform.GATEWAYS_D') +
        platformData.networks.tunnels +
        t('activity:platform.TUNNELS_D')
      );
    } else {
      return t('activity:platform.NETWORK_DESKRIPTION');
    }
  };

  const config = [
    {
      icon: 'group',
      title: t('activity:platform.TEAM'),
      url: 'team',
      description: teamDeskription(),
      disabled:
        platformData.team &&
        (platformData.team.members > 0 || platformData.team.managers > 0)
          ? false
          : true
    },
    {
      icon: 'policies',
      title: t('activity:platform.POLICIES'),
      url: 'policies',
      description: `${
        platformData.policies > 0 ? platformData.policies : 0
      } ${t('activity:platform.POLICIES_D')}`,
      disabled: platformData.policies > 0 ? false : true
    },
    {
      icon: 'network',
      title: t('activity:platform.NETWORKS'),
      notif:
        platformData.networks && platformData.networks.isOnGoingAlertExist
          ? {
              text: `${chooseStatusOnGoing(
                platformData.networks.isOnGoingAlertExist
              )} ${t('activity:platform.MAINTENANCE')}`,
              markerColor: statusColor(
                chooseStatusOnGoing(platformData.networks.isOnGoingAlertExist)
              )
            }
          : false,
      url: 'networks',
      description: networkDeskription(),
      disabled:
        platformData.networks &&
        (platformData.networks.networks > 0 ||
          platformData.networks.regions > 0)
          ? false
          : true
    },
    {
      icon: 'activity',
      title: t('activity:platform.ACTIVITIES'),
      url: 'activities',
      description: `${t('activity:platform.ACTIVITIES')} ${
        platformData.activity ? '' : 'do not'
      } exist`,
      disabled: !platformData.activity
    },
    {
      icon: 'applications',
      title: t('activity:platform.APPLICATIONS'),
      url: 'applications',
      description: `${
        platformData.applications > 0 ? platformData.applications : 0
      } ${t('activity:platform.APPLICATIONS_D')}`,
      disabled: platformData.applications > 0 ? false : true
    },
    {
      icon: 'mobile',
      title: t('activity:platform.DEVICES'),
      url: 'devices',
      description: `${platformData.devices > 0 ? platformData.devices : 0} ${t(
        'activity:platform.DEVICES'
      )}`,
      disabled: platformData.devices > 0 ? false : true
    },
    {
      icon: 'globe',
      title: 'Web Security',
      url: 'platform/swg',
      url2: 'platform/swgBp',
      disabled: platformData.swg
        ? platformData.swg.wf > 0
          ? false
          : true
        : true,
      disabled2: platformData.swg
        ? platformData.swg.bp > 0
          ? false
          : true
        : true,
      description: `${
        platformData.swg ? platformData.swg.wf : ''
      } Web Filter Rules`,
      description2: `${
        platformData.swg ? platformData.swg.bp : ''
      } Bypass Rules`
    },
    {
      icon: 'mobile',
      title: t('activity:platform.DEVICE_SECURITY'),
      url: 'dpc',
      description: `${platformData.dpc > 0 ? platformData.dpc : 0} ${t(
        'activity:platform.DEVICE_SECURITY_DESCRIPTION'
      )}`,
      disabled: platformData.dpc > 0 ? false : true
    }
  ];

  // The platform summary endpoint is only customers:read, so a role without
  // feature-flag read would otherwise click straight into a 403.
  if (hasAccess([resources.PRODUCT_FEATURE_FLAG, access.READ])) {
    config.push({
      icon: 'flag',
      title: t('activity:platform.FEATURE_FLAGS'),
      url: 'feature-flags',
      description: `${platformData.featureFlags || 0} ${t(
        'activity:platform.FEATURE_FLAGS_D'
      )}`
      // Deliberately NOT disabled at 0, unlike the sibling cards. PlatformItems
      // drops the NavLink when disabled is set, and this count is direct
      // assignments only — so a tenant with none, or holding only GA flags,
      // could never open the page. That is precisely the tenant you go in to
      // assign a flag to, and the page is fully populated at 0 because it lists
      // every flag in the region.
    });
  }

  if (loader) return <Loading />;

  return (
    <>
      <PageSubTitle hiden title="Platform page" />
      <PlatformItems items={config} />
    </>
  );
};

Platform.propTypes = {
  customerId: PropTypes.string
};

export default Platform;
